import React from "react"
import { UseOverlaySetValue } from "../../App/useOverlay"
import { createViewContext } from "../../Components/createViewContext"
import { EditGroupData } from "../../Components/EditGroup"
import { EditableList } from "../../Components/useEditableList"
import { ApiClient, INoteAttachment, INoteAttachmentResponse, IShip, IShipData, IShipDataAnchor, IShipDataDevice, IShipDataDevices, IShipDataEngine, IShipDataGenerator, IShipDataInsurance, IShipDataOtherEquipment, IShipDataRigElement, IShipDataSail, IShipDataSteeringSystem, IShipDataTank, IShipUserCrewRole, IShipUserLevel, NoteApi, ShipApi } from "../../Types"

export type TranslateFn = (text: string) => string;

// Predefined enum values per ship-data field. A value is localized only when it is
// one of these known values; anything else is shown verbatim (see predefinedText).
// Canonical home for these lists: they were previously duplicated between the Print
// ship views and the interactive predefined-or-custom Selects, so both sides now
// reference this single source.
export const predefined = {
  insuranceType: ["liability", "hull", "accident"],
  fuelType: ["diesel", "petrol", "gas", "electricity"],
  engineDriveType: ["shaftdrive", "saildrive", "z_drive", "pod_drive", "outboard", "jet_drive"],
  generatorType: ["power_generator", "heating", "air_conditioning", "warm_water", "watermaker"],
  steeringType: ["bow_thruster", "stern_thruster", "self_steering", "windvane", "autopilot"],
  tankType: ["fuel", "fuel_daytank", "water", "gray", "waste", "gas"],
  rigElement: ["mast", "main_mast", "mizzen_mast", "forestay", "stay", "jib_stay", "backstay", "runner", "baby_stay", "kicker", "vang", "winch"],
  sail: ["mainsail", "genoa", "jib", "working_jib", "strom_jib", "spinnaker", "gennaker", "code0", "parasailor", "mizzen", "mizzen_staysail", "try"],
  sailType: ["forestay", "mast", "mizzen"],
  anchorType: ["spade", "fishermans", "plough", "danforth", "rollerbar"],
  deviceTypes: {
    "nautical": ["analogue_compass", "gyro_compass", "fluxgate_compass", "satellite_compass", "chart_plotter", "gps", "ais", "radio", "antenna", "radar", "wind_gauge", "autopilot", "display_instruments", "fuel_gauge", "battery_controller", "hour_meter", "tachometer", "logger", "sounder", "air_temperature", "barometer", "humidity", "weather_receiver", "nmea_gateway", "nmea_motor_adapter"],
    "electric": ["battery", "inverter", "charger", "wind_generator", "solar_panel"],
    "safety": ["fire_extinguisher", "liferaft", "lifejacket", "lifebelt", "lifeline", "spare_propeller", "emergency_tiller", "bilge_pump_manual", "bilge_pump_electric", "shroud_cutter", "radar_reflector", "first_aid", "fire_blanket", "dinghy", "dinghy_motor", "handheld_radio", "epirb"],
  } as Record<string, string[]>,
};

// Vessel-type -> ship-sketch option keys, keyed by IShipVesselType.
export interface VesselTypeOptions {
  [key: number]: string[];
}

// The device map keeps the typed IShipDataDevices shape while allowing indexed
// access by the string category ("electric" | "nautical" | "safety").
export type DevicesMap = IShipDataDevices & { [key: string]: IShipDataDevice[] };

// The data layer for the ship-data editor: every network call the view (or its
// children) makes goes through here so tests can pin the wire contract.
export const ShipDataViewData = {
  attachmentUrl(api: ApiClient, shipId: number, attachment: INoteAttachment, cloudSession: boolean): string {
    return cloudSession
      ? (attachment.url || "")
      : new ShipApi(api).ships.attachmentDownload({ params: { shipId, attachmentId: attachment.id } }).url();
  },
  uploadAttachment(api: ApiClient, shipId: number, attachment: File): Promise<INoteAttachmentResponse> {
    return new NoteApi(api).noteAttachments.create({ params: { shipId, noteId: 0 }, body: { attachment } }).call();
  },

  // --- Shared ship-data derivations (also consumed by the Print ship views) -------------

  // A predefined enum value is localized (`t(prefix + value)`) only when it is one of
  // the known values; free-text values are shown verbatim. Empty/undefined pass through.
  predefinedText(t: TranslateFn, list: string[], value: string | undefined, prefix: string): string | undefined {
    return value ? (list.includes(value) ? t(`${prefix}${value}`) : value) : value;
  },

  // Engines render `engineCount` rows; missing entries fall back to a blank placeholder.
  engineAt(shipData: IShipData | undefined, index: number): IShipDataEngine {
    return shipData?.engines?.[index] || ({
      id: `engine-${index}`,
      number: index + 1,
      nmeaNumber: undefined,
      engineShortName: "",
      manufacturer: "",
      model: "",
      serialNumber: "",
      power: undefined,
      powerUnit: undefined,
      fuelType: "",
      driveType: "",
      propellerType: "",
    } as IShipDataEngine);
  },

  crewRoleText(tt: TranslateFn, role: IShipUserCrewRole): string {
    switch (role) {
      case IShipUserCrewRole.none: return tt("ship_user_crew_role_none");
      case IShipUserCrewRole.crew: return tt("ship_user_crew_role_crew");
      case IShipUserCrewRole.skipper: return tt("ship_user_crew_role_skipper");
      case IShipUserCrewRole.owner: return tt("ship_user_crew_role_owner");
      default: return String(role);
    }
  },

  userLevelText(tt: TranslateFn, level: IShipUserLevel): string {
    switch (level) {
      case IShipUserLevel.none: return tt("ship_user_level_none");
      case IShipUserLevel.viewCurrent: return tt("ship_user_level_viewCurrent");
      case IShipUserLevel.viewAll: return tt("ship_user_level_viewAll");
      case IShipUserLevel.editCurrent: return tt("ship_user_level_editCurrent");
      case IShipUserLevel.editAll: return tt("ship_user_level_editAll");
      case IShipUserLevel.primary: return tt("ship_user_level_primary");
      default: return String(level);
    }
  },

  // The crew roles a person can actually be given. `owner` is deliberately absent:
  // it is server-assigned to the ship's creator and can never be granted through
  // the API, so offering it would be a dead option (#1215). Single source for the
  // app view, the catalog harness and the catalog registry.
  selectableCrewRoles: [IShipUserCrewRole.none, IShipUserCrewRole.crew, IShipUserCrewRole.skipper],

  crewRoleOptions(tt: TranslateFn): React.ReactNode {
    return ShipDataViewData.selectableCrewRoles.map((role) => (
      <option value={role} key={role}>{ShipDataViewData.crewRoleText(tt, role)}</option>
    ));
  },

  userLevelOptions(tt: TranslateFn): React.ReactNode {
    return Object.values(IShipUserLevel).filter((level) => typeof level === "number").map((level) => (
      <option value={level} key={level}>{ShipDataViewData.userLevelText(tt, level as IShipUserLevel)}</option>
    ));
  },

  // A row that holds an unknown legacy role still needs a matching `<option>`,
  // or its `<select>` would render blank. The known non-selectable `owner` role
  // is rendered read-only by ShipUserEditView (#1543).
  legacyCrewRoleOption(tt: TranslateFn, role: IShipUserCrewRole): React.ReactNode {
    if (ShipDataViewData.selectableCrewRoles.includes(role)) {
      return null;
    }
    return <option value={role} key={role}>{ShipDataViewData.crewRoleText(tt, role)}</option>;
  },
};

// Shared runtime state for the ship-data editor. The root ShipDataView keeps all
// hooks and publishes them here so each tab child can render its slice without
// prop-drilling. Kept cycle-free (imports only React + Types) so the leaf views
// can pull the context without importing the root view.
export interface ShipDataState {
  data: IShipData;
  ship: IShip;
  attachments?: INoteAttachment[];

  editable: boolean;
  formEditable: boolean;

  edited: boolean;
  onEdited: () => void;
  save: (data: FormData, values: EditGroupData) => Promise<void>;
  saveData: (values: EditGroupData, namePrefix?: string) => Promise<void>;

  newId: () => string;

  // Photo / attachment upload state (single image).
  files: File[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  saveAttachments: (data: FormData, values: any) => Promise<void>;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeAttachment: (event: React.MouseEvent<HTMLDivElement>) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  showAttachment: number;
  setShowAttachment: React.Dispatch<React.SetStateAction<number>>;

  // Equipment collections as editable lists (items + add / removeAt helpers).
  editInsurance: EditableList<IShipDataInsurance>;
  editGenerator: EditableList<IShipDataGenerator>;
  editSail: EditableList<IShipDataSail>;
  editTank: EditableList<IShipDataTank>;
  editAnchor: EditableList<IShipDataAnchor>;
  editSteeringSystem: EditableList<IShipDataSteeringSystem>;
  editOtherEquipment: EditableList<IShipDataOtherEquipment>;
  editRigElement: EditableList<IShipDataRigElement>;

  // Engines are derived from ship.engineCount (no add/remove), but share the shape.
  editEngine: EditableList<IShipDataEngine>;

  // Devices are grouped by category rather than a flat array.
  devices: DevicesMap;
  deviceTypes: string[];
  addDevice: (type: string) => void;
  removeDevice: (type: string, index: number) => void;

  // Vessel-type sketch selection (details tab).
  selectedShipType: string;
  setSelectedShipType: React.Dispatch<React.SetStateAction<string>>;
  shipType?: string;
  vesselTypeOptions: VesselTypeOptions;

  // Ship-user tab statics (the roster itself now comes from the UsersView data boundary).
  shipUserCrewRoleOptions: React.ReactNode;
  shipUserLevelOptions: React.ReactNode;
  newUserOverlay: boolean;
  setNewUserOverlay: UseOverlaySetValue<boolean, undefined>;
  // Whether the current user can create/update ship users (UserApi.shipUsers.create/update,
  // requires .primary) — distinct from the generic `editable`, which only reflects the ship's
  // own data being editable, not this specific permission (#931, permission model, tracker #827).
  shipUsersManageable: boolean;
}

const shipDataContext = createViewContext<ShipDataState>();
export const ShipDataContext = shipDataContext.Context;
export const GetShipData = shipDataContext.useGet;
